/**
 * 全局通知权限管理器
 * 负责在应用启动时检测和请求通知权限
 */

export interface GlobalNotificationState {
    permission: NotificationPermission;
    isSupported: boolean;
    isInitialized: boolean;
}

class GlobalNotificationManager {
    private state: GlobalNotificationState = {
        permission: 'default',
        isSupported: false,
        isInitialized: false
    };

    private listeners: Array<(state: GlobalNotificationState) => void> = [];

    /**
     * 初始化通知管理器
     */
    async initialize(): Promise<void> {
        console.log('🔔 初始化全局通知管理器...');

        // 检查浏览器支持
        this.state.isSupported = 'Notification' in window;

        if (!this.state.isSupported) {
            console.warn('⚠️ 浏览器不支持通知API');
            this.state.isInitialized = true;
            this.notifyListeners();
            return;
        }

        // 获取当前权限状态
        this.state.permission = Notification.permission;
        console.log(`🔐 当前通知权限状态: ${this.state.permission}`);

        // 如果权限未确定，尝试请求权限
        if (this.state.permission === 'default') {
            await this.requestPermissionWithDelay();
        }

        this.state.isInitialized = true;
        this.notifyListeners();

        console.log('✅ 全局通知管理器初始化完成');
    }

    /**
     * 延迟请求权限（避免在页面加载时立即弹出权限请求）
     */
    private async requestPermissionWithDelay(): Promise<void> {
        // 延迟3秒后请求权限，给用户一些时间适应界面
        setTimeout(async () => {
            try {
                console.log('🔑 请求通知权限...');
                const permission = await Notification.requestPermission();
                this.state.permission = permission;

                if (permission === 'granted') {
                    console.log('✅ 通知权限已获取');
                    // 显示一个欢迎通知
                    this.showWelcomeNotification();
                } else {
                    console.log('❌ 通知权限被拒绝');
                }

                this.notifyListeners();
            } catch (error) {
                console.error('❌ 请求通知权限失败:', error);
            }
        }, 3000);
    }

    /**
     * 显示欢迎通知
     */
    private showWelcomeNotification(): void {
        if (this.state.permission === 'granted') {
            try {
                const notification = new Notification('通知已启用', {
                    body: '您现在可以接收桌面通知了',
                    icon: '/favicon.ico',
                    tag: 'welcome-notification',
                    requireInteraction: false,
                    silent: true, // 欢迎通知静音
                });

                // 2秒后自动关闭
                setTimeout(() => {
                    notification.close();
                }, 2000);

                notification.onclick = () => {
                    window.focus();
                    notification.close();
                };
            } catch (error) {
                console.error('显示欢迎通知失败:', error);
            }
        }
    }

    /**
     * 手动请求权限（用于用户主动操作）
     */
    async requestPermission(): Promise<NotificationPermission> {
        if (!this.state.isSupported) {
            console.warn('浏览器不支持通知API');
            return 'denied';
        }

        if (this.state.permission === 'granted') {
            return 'granted';
        }

        try {
            console.log('🔑 手动请求通知权限...');
            const permission = await Notification.requestPermission();
            this.state.permission = permission;
            this.notifyListeners();

            console.log(`🔐 权限请求结果: ${permission}`);
            return permission;
        } catch (error) {
            console.error('请求通知权限失败:', error);
            return 'denied';
        }
    }

    /**
     * 获取当前状态
     */
    getState(): GlobalNotificationState {
        return { ...this.state };
    }

    /**
     * 检查是否有权限
     */
    hasPermission(): boolean {
        return this.state.isSupported && this.state.permission === 'granted';
    }

    /**
     * 检查是否支持通知
     */
    isSupported(): boolean {
        return this.state.isSupported;
    }

    /**
     * 添加状态监听器
     */
    addListener(callback: (state: GlobalNotificationState) => void): () => void {
        this.listeners.push(callback);

        // 返回取消监听的函数
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    /**
     * 通知所有监听器
     */
    private notifyListeners(): void {
        this.listeners.forEach(listener => {
            try {
                listener(this.getState());
            } catch (error) {
                console.error('通知状态监听器执行失败:', error);
            }
        });
    }

    /**
     * 重置权限状态（用于测试）
     */
    reset(): void {
        this.state = {
            permission: 'default',
            isSupported: 'Notification' in window,
            isInitialized: false
        };
        this.listeners = [];
    }
}

// 创建全局单例
export const globalNotificationManager = new GlobalNotificationManager();

// 导出便捷函数
export const initializeGlobalNotifications = () => globalNotificationManager.initialize();
export const requestGlobalNotificationPermission = () => globalNotificationManager.requestPermission();
export const hasNotificationPermission = () => globalNotificationManager.hasPermission();
export const isNotificationSupported = () => globalNotificationManager.isSupported();
export const getGlobalNotificationState = () => globalNotificationManager.getState();
export const addNotificationStateListener = (callback: (state: GlobalNotificationState) => void) =>
    globalNotificationManager.addListener(callback);
